import { useEffect, useRef } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { syncNow } from '../utils/firestoreSync';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { checkFeatureEnabled } from '../utils/features';
import { logger } from '../utils/logger';
import { getPlatform, StorageChangeMap } from '../infrastructure/platform';

/**
 * Drives Firestore sync from whichever extension surface is open
 * (popup / side panel / settings / fullscreen). Renders nothing.
 *
 * Triggers a sync:
 *  - when the user becomes signed in,
 *  - on a periodic interval,
 *  - shortly after local prompts/folders change (debounced).
 *
 * Cloud sync is a premium feature: every trigger re-checks the `cloudSync`
 * feature gate, so free signed-in users don't sync (matching the Settings UI).
 * Failures are caught and logged with exponential backoff instead of becoming
 * unhandled rejections, and cycles are skipped while offline.
 *
 * Only mounted when the cloud features are enabled (see `Providers`).
 * Background-context sync (service worker) is a deliberate follow-up — the
 * Firebase Auth session lives in the UI contexts here.
 */

const SYNC_INTERVAL_MS = 2 * 60 * 1000;
const CHANGE_DEBOUNCE_MS = 1500;
const BACKOFF_BASE_MS = 30 * 1000;
const BACKOFF_MAX_MS = 15 * 60 * 1000;

export function ApiSyncManager() {
  const { session } = useAuthContext();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failuresRef = useRef(0);
  const backoffUntilRef = useRef(0);

  useEffect(() => {
    if (!session) return;

    const run = async () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      if (Date.now() < backoffUntilRef.current) return;
      try {
        if (!(await checkFeatureEnabled('cloudSync'))) return;
        await syncNow();
        failuresRef.current = 0;
        backoffUntilRef.current = 0;
      } catch (error) {
        failuresRef.current += 1;
        const backoff = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (failuresRef.current - 1));
        backoffUntilRef.current = Date.now() + backoff;
        logger.error(
          'Cloud sync cycle failed',
          error instanceof Error ? error : new Error(String(error)),
          {
            module: 'ApiSyncManager',
            function: 'run',
            consecutiveFailures: failuresRef.current,
            backoffMs: backoff,
          }
        );
      }
    };
    const trigger = () => {
      void run();
    };

    // Initial sync on sign-in.
    trigger();

    // Periodic sync.
    const interval = setInterval(trigger, SYNC_INTERVAL_MS);

    // Sync shortly after local prompt/folder changes (push-through).
    const onChanged = (changes: StorageChangeMap) => {
      // SETTINGS is watched so the premium flip (webhook → background
      // reconcile → settings write) triggers the first sync immediately
      // instead of waiting out the interval; the cloudSync gate re-check in
      // run() makes unrelated settings changes harmless.
      if (
        STORAGE_KEYS.PROMPTS in changes ||
        STORAGE_KEYS.FOLDERS in changes ||
        STORAGE_KEYS.SETTINGS in changes
      ) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(trigger, CHANGE_DEBOUNCE_MS);
      }
    };
    const unsubscribe = getPlatform().storage.onChanged(onChanged);

    return () => {
      clearInterval(interval);
      unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [session]);

  return null;
}
